import { z } from 'zod';
import { BUSINESS_RULES } from './rules.js';
/**
 * ISO-8601 instant in UTC, e.g. `2026-10-25T00:30:00.000Z`.
 *
 * Branded on purpose: a plain `string` cannot be passed where an instant is
 * expected, so a local-time or epoch-millis value cannot silently cross the API
 * boundary. Use {@link toIsoInstant} to build one.
 */
export const IsoInstantSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/, 'must be an ISO-8601 UTC instant ending with Z')
    .brand();
export function toIsoInstant(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new TypeError(`not a valid instant: ${String(value)}`);
    }
    return date.toISOString();
}
export function fromIsoInstant(value) {
    return new Date(value);
}
/** Money in CZK, serialised as a fixed-point string so precision never depends on IEEE-754. */
export const MoneyCzkSchema = z
    .string()
    .regex(/^-?\d{1,12}\.\d{2}$/, 'must be a decimal amount with exactly 2 decimal places')
    .brand();
export function toMoneyCzk(value) {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) {
        throw new TypeError(`not a valid amount: ${String(value)}`);
    }
    return num.toFixed(2);
}
export const IdSchema = z.coerce.number().int().positive();
export const UuidSchema = z.string().uuid();
export const PageQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export function pageResponseSchema(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().nonnegative(),
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        hasNext: z.boolean(),
    });
}
/** One already-booked interval that blocks a candidate reservation. */
export const ConflictSchema = z.object({
    reservationId: UuidSchema,
    state: z.string(),
    startAt: IsoInstantSchema,
    endAt: IsoInstantSchema,
    /** Present when the conflict comes from the turnaround buffer rather than the interval itself. */
    turnaroundBufferMinutes: z.number().int().nonnegative().optional(),
});
export const RuleViolationSchema = z.object({
    rule: z.enum(BUSINESS_RULES),
    message: z.string(),
    detail: z.string().optional(),
});
/**
 * RFC 7807 problem details — the single error shape of this API.
 *
 * The frontend never inspects an untyped exception: it parses the response body
 * with this schema and renders `violatedRule` via RULE_LABELS.
 */
export const ProblemDetailsSchema = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    instance: z.string().optional(),
    violatedRule: z.enum(BUSINESS_RULES).optional(),
    conflicts: z.array(ConflictSchema).optional(),
    violations: z.array(RuleViolationSchema).optional(),
    /** Field-level validation errors: path -> messages. */
    errors: z.record(z.string(), z.array(z.string())).optional(),
});
export const MessageResponseSchema = z.object({ message: z.string() });
//# sourceMappingURL=common.js.map